import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { selectParametersDataState } from './parameters-data.selectors';
import { filter, finalize, map, take } from 'rxjs/operators';
import {
  loadParametersDataFailureByKeys,
  loadParametersDataSuccessMany,
  requestParametersDataByKeys,
} from './parameters-data.actions';
import {
  ParameterData,
  ParametersDataResponseRequest,
} from 'src/app/pages/admin-manager/parameters/models/parameters.model';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IdbParameterDataEntry } from 'src/app/idb/AppDB';

const IDB_ELIGIBLE_SCOPE_TYPE_IDS: ReadonlySet<number> = new Set<number>([4, 5]);

@Injectable({ providedIn: 'root' })
export class ParametersDataApiService {
  private api = environment.APIM + environment.newOrderAPI + 'Parameter/GetParametersData/';
  private readonly inFlightKeys = new Set<string>();

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private idb: ManageIdbService,
  ) {}

  /**
   * Returns a JSON string array (same format the endpoint returns) in the same order as requested.
   * Lookup order: 1) NgRx store cache  2) IndexedDB  3) HTTP POST.
   * After HTTP responds, results are saved to both IndexedDB and NgRx store.
   */
  getParametersData(payload: ParametersDataResponseRequest | ParametersDataResponseRequest[]): Observable<string> {
    const rawRequests: ParametersDataResponseRequest[] = Array.isArray(payload) ? payload : [payload];
    const requests: ParametersDataResponseRequest[] = rawRequests.map((r) => this.normalizeRequest(r));
    const keys = requests.map((r) => this.buildParamKey(r));

    this.store
      .select(selectParametersDataState)
      .pipe(take(1))
      .subscribe((state) => {
        const cache = state.cache || {};
        const loadingByKey = state.loadingByKey || {};

        const missingKeys: string[] = [];
        const missingRequests: ParametersDataResponseRequest[] = [];
        const missingKeySet = new Set<string>();

        for (let i = 0; i < requests.length; i++) {
          const key = keys[i];
          if (cache[key]) continue;
          if (loadingByKey[key]) continue;
          if (this.inFlightKeys.has(key)) continue;
          if (missingKeySet.has(key)) continue;
          missingKeys.push(key);
          missingRequests.push(requests[i]);
          missingKeySet.add(key);
        }

        if (missingKeys.length === 0) return;

        // Check IndexedDB for missing keys before going to HTTP
        this.idb
          .getParametersData(missingKeys)
          .then((idbResults) => {
            const idbEntries: { key: string; response: ParameterData }[] = [];
            const toFetch: ParametersDataResponseRequest[] = [];
            const toFetchKeys: string[] = [];

            for (let i = 0; i < missingKeys.length; i++) {
              const idbEntry = idbResults[i];
              if (idbEntry && idbEntry.parameterValue !== undefined) {
                // Found in IDB — promote to NgRx cache
                idbEntries.push({
                  key: missingKeys[i],
                  response: {
                    parameterCode: idbEntry.parameterCode,
                    parameterValue: idbEntry.parameterValue,
                    scopeId: idbEntry.scopeId ?? undefined,
                  },
                });
              } else {
                // Not in IDB — needs HTTP
                if (!this.inFlightKeys.has(missingKeys[i])) {
                  toFetch.push(missingRequests[i]);
                  toFetchKeys.push(missingKeys[i]);
                }
              }
            }

            // Promote IDB hits to NgRx store
            if (idbEntries.length > 0) {
              this.store.dispatch(loadParametersDataSuccessMany({ entries: idbEntries }));
            }

            // If everything was in IDB, done
            if (toFetchKeys.length === 0) return;

            this.fetchAndCacheParameters(toFetch, toFetchKeys);
          })
          .catch(() => {
            const toFetch: ParametersDataResponseRequest[] = [];
            const toFetchKeys: string[] = [];
            for (let i = 0; i < missingKeys.length; i++) {
              if (!this.inFlightKeys.has(missingKeys[i])) {
                toFetch.push(missingRequests[i]);
                toFetchKeys.push(missingKeys[i]);
              }
            }

            this.fetchAndCacheParameters(toFetch, toFetchKeys);
          });
      });

    return this.store.select(selectParametersDataState).pipe(
      map((state) => {
        const errorByKey = state.errorByKey || {};
        for (const key of keys) {
          if (errorByKey[key]) {
            throw errorByKey[key];
          }
        }
        const cache = state.cache || {};
        const values = keys.map((k) => cache[k] ?? null);
        return values;
      }),
      filter((values) => values.every((v) => !!v)),
      take(1),
      map((values) => JSON.stringify(values)),
    );
  }

  private buildParamKey(req: ParametersDataResponseRequest): string {
    const code = String(req?.parameterCode ?? '');
    const scopeId = this.normalizeScopeId(req?.scopeId);
    const scopeTypeId = Number(req?.parameterScopeTypeId);
    return `${code}|${scopeId ?? 'null'}|${scopeTypeId}`;
  }

  private normalizeRequest(input: ParametersDataResponseRequest): ParametersDataResponseRequest {
    const asRecord = input as unknown as Record<string, unknown>;
    const parameterCode = (asRecord['parameterCode'] ?? asRecord['ParameterCode']) as string;
    const parameterScopeTypeId = (asRecord['parameterScopeTypeId'] ?? asRecord['ParameterScopeTypeId']) as number;
    const scopeId = (asRecord['scopeId'] ?? asRecord['ScopeId']) as number | undefined;

    return {
      parameterCode,
      parameterScopeTypeId,
      scopeId,
    };
  }

  private fetchAndCacheParameters(toFetch: ParametersDataResponseRequest[], toFetchKeys: string[]): void {
    if (toFetchKeys.length === 0) return;

    for (const key of toFetchKeys) {
      this.inFlightKeys.add(key);
    }

    this.store.dispatch(requestParametersDataByKeys({ keys: toFetchKeys }));

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Ocp-Apim-Subscription-Key', environment.apimSubscriptionKey);

    this.http
      .post<string>(this.api, toFetch, { headers, responseType: 'text' as 'json' })
      .pipe(
        take(1),
        finalize(() => {
          for (const key of toFetchKeys) {
            this.inFlightKeys.delete(key);
          }
        }),
      )
      .subscribe({
        next: (responseText) => {
          const responseArray = this.safeParseParameterDataArray(responseText);
          const responseMap = new Map<string, ParameterData>();
          for (const item of responseArray) {
            const lookupKey = this.buildResponseLookupKey(item.parameterCode, item.scopeId);
            responseMap.set(lookupKey, item);
          }

          const entries: { key: string; response: ParameterData }[] = [];
          const idbEntriesToSave: IdbParameterDataEntry[] = [];

          for (let idx = 0; idx < toFetch.length; idx++) {
            const req = toFetch[idx];
            const lookupKey = this.buildResponseLookupKey(req.parameterCode, req.scopeId);
            const hit = responseMap.get(lookupKey);
            const normalizedScopeId = this.normalizeScopeId(req.scopeId);
            const response: ParameterData = {
              parameterCode: String(req.parameterCode),
              parameterValue: hit?.parameterValue ?? '',
              scopeId: hit?.scopeId ?? normalizedScopeId ?? undefined,
            };
            entries.push({ key: toFetchKeys[idx], response });

            idbEntriesToSave.push({
              key: toFetchKeys[idx],
              parameterCode: String(req.parameterCode),
              parameterValue: hit?.parameterValue ?? '',
              scopeId: normalizedScopeId,
              parameterScopeTypeId: Number(req.parameterScopeTypeId),
            });
          }

          this.store.dispatch(loadParametersDataSuccessMany({ entries }));
          const idbEligibleEntries = idbEntriesToSave.filter((entry) =>
            IDB_ELIGIBLE_SCOPE_TYPE_IDS.has(entry.parameterScopeTypeId),
          );
          if (idbEligibleEntries.length > 0) {
            this.idb.saveParametersData(idbEligibleEntries).catch(() => undefined);
          }
        },
        error: (error) => {
          this.store.dispatch(loadParametersDataFailureByKeys({ keys: toFetchKeys, error }));
        },
      });
  }

  private normalizeScopeId(scopeId: unknown): number | null {
    if (scopeId === null || scopeId === undefined || scopeId === '') return null;
    const n = Number(scopeId);
    return Number.isFinite(n) ? n : null;
  }

  private buildResponseLookupKey(parameterCode: unknown, scopeId: unknown): string {
    const code = String(parameterCode ?? '');
    const normalizedScopeId = this.normalizeScopeId(scopeId);
    return `${code}|${normalizedScopeId ?? 'null'}`;
  }

  private safeParseParameterDataArray(raw: string): ParameterData[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as ParameterData[]) : [];
    } catch {
      return [];
    }
  }
}
